import React from 'react';
import {
  PERMISSION_MODULE_GROUPS,
  PERMISSION_ACTIONS,
} from '../utils/permissions';

/**
 * Permissions matrix — a checkbox grid of (module × action).
 *
 * Props:
 *  - value: { [moduleKey]: { view, create, edit, delete } }
 *  - onChange(nextValue)
 *  - disabled (optional)
 */
const PermissionsMatrix = ({ value = {}, onChange, disabled = false }) => {
  const setCell = (moduleKey, action, next) => {
    const current = value?.[moduleKey] || {
      view: false,
      create: false,
      edit: false,
      delete: false,
    };
    const updated = { ...current, [action]: next };
    // If user enables any action, enable view too (you can't act without seeing)
    if (next && action !== 'view') updated.view = true;
    // If user disables view, disable everything else
    if (action === 'view' && !next) {
      updated.create = false;
      updated.edit = false;
      updated.delete = false;
    }
    onChange({ ...value, [moduleKey]: updated });
  };

  const toggleAllForModule = (moduleKey, next) => {
    const updated = { view: next, create: next, edit: next, delete: next };
    onChange({ ...value, [moduleKey]: updated });
  };

  return (
    <div className="border rounded-lg overflow-hidden" data-testid="permissions-matrix">
      {PERMISSION_MODULE_GROUPS.map((group, gi) => (
        <div key={group.label}>
          <div className={`px-3 py-2 bg-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-600 ${gi > 0 ? 'border-t' : ''}`}>
            {group.label}
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600 w-1/2">Module</th>
                {PERMISSION_ACTIONS.map((a) => (
                  <th
                    key={a.key}
                    className="px-2 py-2 text-center font-medium text-gray-600 w-16"
                  >
                    {a.label}
                  </th>
                ))}
                <th className="px-2 py-2 text-center font-medium text-gray-500 w-16">All</th>
              </tr>
            </thead>
            <tbody>
              {group.modules.map((m) => {
                const cell = value?.[m.key] || {};
                const allOn = !!cell.view && !!cell.create && !!cell.edit && !!cell.delete;
                return (
                  <tr key={m.key} className="border-t hover:bg-blue-50/40">
                    <td className="px-3 py-2 text-gray-800">
                      <span className="mr-2">{m.icon}</span>
                      {m.label}
                    </td>
                    {PERMISSION_ACTIONS.map((a) => (
                      <td key={a.key} className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          disabled={disabled}
                          checked={!!cell[a.key]}
                          onChange={(e) => setCell(m.key, a.key, e.target.checked)}
                          className="w-4 h-4 cursor-pointer accent-blue-600"
                          data-testid={`perm-${m.key}-${a.key}`}
                        />
                      </td>
                    ))}
                    <td className="px-2 py-2 text-center">
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={allOn}
                        onChange={(e) => toggleAllForModule(m.key, e.target.checked)}
                        className="w-4 h-4 cursor-pointer accent-emerald-600"
                        data-testid={`perm-${m.key}-all`}
                        title="Toggle all actions"
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      {/* Special toggles — sit outside the CRUD grid */}
      <div className="border-t bg-amber-50/30">
        <div className="px-3 py-2 bg-gray-100 text-xs font-semibold uppercase tracking-wide text-gray-600 border-t">
          Special Actions
        </div>
        <div className="px-3 py-3 text-sm">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              disabled={disabled}
              checked={!!value?.approvals?.can_approve}
              onChange={(e) =>
                onChange({
                  ...value,
                  approvals: { ...(value?.approvals || {}), can_approve: e.target.checked },
                })
              }
              className="w-4 h-4 cursor-pointer accent-amber-600"
              data-testid="perm-approvals-can_approve"
            />
            <span className="font-medium">🛡️ Can approve documents</span>
            <span className="text-xs text-gray-500">
              — eligible to be added to PO &amp; Invoice approval pools
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default PermissionsMatrix;
